import React, { useState, useEffect } from "react";
import {
  MdSchedule,
  MdPerson,
  MdGroup,
  MdLocationOn,
  MdEventRepeat,
  MdTimer,
  MdCelebration,
} from "react-icons/md";
import AtriumApi from "api";
import Badge from "components/badge/Badge";
import AlertPane from "components/alert";
import Loader from "components/loader/Loader";
import { Checkbox, FormField, getControlClassName } from "components/fields";
import ChoiceCard from "components/heartbeat/ChoiceCard";
import OptionGroup from "components/heartbeat/OptionGroup";

/**
 * Trigger type definitions with their display info and config fields
 */
const TRIGGER_TYPES = {
  member_creation: {
    label: "New Member Creation",
    description: "Send survey when a new member is created",
    icon: MdPerson,
    type: "event",
    configFields: [],
  },
  first_checkin: {
    label: "First Check-in",
    description: "Send survey after a member's first attendance check-in",
    icon: MdTimer,
    type: "event",
    configFields: [],
  },
  days_after_joining: {
    label: "Days After Joining",
    description: "Send survey X days after member joined",
    icon: MdSchedule,
    type: "scheduled",
    configFields: ["delayDays"],
  },
  group_joined: {
    label: "Group Joined",
    description: "Send survey when member joins specific group(s)",
    icon: MdGroup,
    type: "event",
    configFields: ["groupIds", "delayHours"],
  },
  membership_anniversary: {
    label: "Membership Anniversary",
    description: "Send survey on membership anniversary (yearly)",
    icon: MdCelebration,
    type: "scheduled",
    configFields: ["yearsInterval"],
  },
  inactivity: {
    label: "Inactivity Re-engagement",
    description: "Send survey after X days of no check-ins",
    icon: MdEventRepeat,
    type: "scheduled",
    configFields: ["inactivityDays"],
  },
  campus_assignment: {
    label: "Campus Assignment",
    description: "Send survey when assigned to specific campus(es)",
    icon: MdLocationOn,
    type: "event",
    configFields: ["campusIds"],
  },
};

const PANEL_CLASS = "rounded-control bg-surface-raised p-4";

const parseNumber = (value) => (value ? parseInt(value) : null);

/**
 * NumberSentence - Labelled number input shown inline in a sentence ("Send survey [7] days after joining")
 */
const NumberSentence = ({ label, before, after, hint, value, onChange, min, max, placeholder, widthClass = "w-20" }) => (
  <div className={PANEL_CLASS}>
    <FormField label={label} hint={hint}>
      {(controlProps) => (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-fg-muted">{before}</span>
          <div className={widthClass}>
            <input
              type="number"
              min={min}
              max={max}
              value={value || ""}
              onChange={(e) => onChange(parseNumber(e.target.value))}
              placeholder={placeholder}
              {...controlProps}
              className={getControlClassName({ invalid: controlProps["aria-invalid"] })}
            />
          </div>
          <span className="text-sm text-fg-muted">{after}</span>
        </div>
      )}
    </FormField>
  </div>
);

/**
 * TriggerTypeSelector - Cards for selecting trigger type
 */
const TriggerTypeSelector = ({ selectedType, onChange, disabled }) => {
  const types = Object.entries(TRIGGER_TYPES);

  return (
    <OptionGroup label="Trigger Type">
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {types.map(([key, config]) => (
          <ChoiceCard
            key={key}
            selected={selectedType === key}
            onClick={() => !disabled && onChange(key)}
            disabled={disabled}
            icon={config.icon}
            title={config.label}
            description={config.description}
          >
            <Badge tone={config.type === "event" ? "success" : "info"} className="mt-2">
              {config.type === "event" ? "Real-time" : "Scheduled"}
            </Badge>
          </ChoiceCard>
        ))}
      </div>
    </OptionGroup>
  );
};

/**
 * DaysAfterJoiningConfig - Config for days_after_joining trigger
 */
const DaysAfterJoiningConfig = ({ config, onChange }) => (
  <NumberSentence
    label="Delay Days"
    before="Send survey"
    after="days after joining"
    hint="Survey will be queued for users who joined exactly this many days ago. Processed daily."
    min="1"
    max="365"
    placeholder="7"
    value={config.delayDays}
    onChange={(delayDays) => onChange({ ...config, delayDays })}
  />
);

/**
 * InactivityConfig - Config for inactivity trigger
 */
const InactivityConfig = ({ config, onChange }) => (
  <NumberSentence
    label="Inactivity Period"
    before="Send survey after"
    after="days of no check-ins"
    hint="Helps re-engage members who haven't attended recently. Processed daily."
    min="7"
    max="365"
    placeholder="60"
    value={config.inactivityDays}
    onChange={(inactivityDays) => onChange({ ...config, inactivityDays })}
  />
);

/**
 * MembershipAnniversaryConfig - Config for membership_anniversary trigger
 */
const MembershipAnniversaryConfig = ({ config, onChange }) => (
  <NumberSentence
    label="Anniversary Interval"
    before="Send survey every"
    after="year(s) on anniversary"
    hint="Example: Set to 1 for annual surveys, 2 for bi-annual. Processed daily on anniversary date."
    min="1"
    max="10"
    placeholder="1"
    widthClass="w-16"
    value={config.yearsInterval}
    onChange={(yearsInterval) => onChange({ ...config, yearsInterval })}
  />
);

/**
 * GroupJoinedConfig - Config for group_joined trigger
 */
const GroupJoinedConfig = ({ config, onChange, groups = [] }) => {
  const toggleGroup = (groupId) => {
    const currentIds = config.groupIds || [];
    if (currentIds.includes(groupId)) {
      onChange({
        ...config,
        groupIds: currentIds.filter((id) => id !== groupId),
      });
    } else {
      onChange({
        ...config,
        groupIds: [...currentIds, groupId],
      });
    }
  };

  return (
    <div className="space-y-4">
      <OptionGroup
        label="Target Groups (optional)"
        hint="Select specific groups to trigger on. Leave empty to trigger for any group."
        className={PANEL_CLASS}
      >
        <div className="max-h-48 overflow-y-auto">
          {groups.length === 0 ? (
            <p className="text-sm text-fg-muted">No groups available</p>
          ) : (
            <div className="space-y-2">
              {groups.map((group) => (
                <Checkbox
                  key={group.id}
                  label={group.name}
                  checked={(config.groupIds || []).includes(group.id)}
                  onChange={() => toggleGroup(group.id)}
                />
              ))}
            </div>
          )}
        </div>
      </OptionGroup>

      <NumberSentence
        label="Delay (optional)"
        before="Wait"
        after="hours before sending"
        min="0"
        max="168"
        placeholder="0"
        value={config.delayHours}
        onChange={(delayHours) => onChange({ ...config, delayHours })}
      />
    </div>
  );
};

/**
 * CampusAssignmentConfig - Config for campus_assignment trigger
 */
const CampusAssignmentConfig = ({ config, onChange, campuses = [] }) => {
  const toggleCampus = (campusId) => {
    const currentIds = config.campusIds || [];
    if (currentIds.includes(campusId)) {
      onChange({
        ...config,
        campusIds: currentIds.filter((id) => id !== campusId),
      });
    } else {
      onChange({
        ...config,
        campusIds: [...currentIds, campusId],
      });
    }
  };

  return (
    <OptionGroup
      label="Target Campuses (optional)"
      hint="Select specific campuses to trigger on. Leave empty to trigger for any campus assignment."
      className={PANEL_CLASS}
    >
      <div className="space-y-2">
        {campuses.length === 0 ? (
          <p className="text-sm text-fg-muted">No campuses available</p>
        ) : (
          campuses.map((campus) => (
            <Checkbox
              key={campus.id}
              label={
                <>
                  {campus.name}
                  {campus.isPrimary && (
                    <span className="ml-1 text-xs font-normal text-fg-muted">(Primary)</span>
                  )}
                </>
              }
              checked={(config.campusIds || []).includes(campus.id)}
              onChange={() => toggleCampus(campus.id)}
            />
          ))
        )}
      </div>
    </OptionGroup>
  );
};

/**
 * TriggerConfiguration - Main component for configuring automatic triggers
 */
const TriggerConfiguration = ({
  triggerType,
  triggerConfig,
  onTriggerTypeChange,
  onTriggerConfigChange,
  disabled = false,
}) => {
  const [filterOptions, setFilterOptions] = useState({
    campuses: [],
    groups: [],
  });
  const [loading, setLoading] = useState(true);

  // Fetch filter options (campuses, groups) on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const response = await AtriumApi.get("/v2/heartbeat/filter-options");
        if (response.data.success) {
          setFilterOptions({
            campuses: response.data.data.campuses || [],
            groups: response.data.data.groups || [],
          });
        }
      } catch (error) {
        console.error("Error fetching filter options:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOptions();
  }, []);

  // Render config based on trigger type
  const renderTriggerConfig = () => {
    if (!triggerType) return null;

    const typeConfig = TRIGGER_TYPES[triggerType];
    if (!typeConfig || typeConfig.configFields.length === 0) {
      return (
        <p className={`${PANEL_CLASS} text-sm text-fg-muted`}>
          No additional configuration needed for this trigger type.
        </p>
      );
    }

    switch (triggerType) {
      case "days_after_joining":
        return (
          <DaysAfterJoiningConfig
            config={triggerConfig || {}}
            onChange={onTriggerConfigChange}
          />
        );
      case "inactivity":
        return (
          <InactivityConfig
            config={triggerConfig || {}}
            onChange={onTriggerConfigChange}
          />
        );
      case "membership_anniversary":
        return (
          <MembershipAnniversaryConfig
            config={triggerConfig || {}}
            onChange={onTriggerConfigChange}
          />
        );
      case "group_joined":
        return (
          <GroupJoinedConfig
            config={triggerConfig || {}}
            onChange={onTriggerConfigChange}
            groups={filterOptions.groups}
          />
        );
      case "campus_assignment":
        return (
          <CampusAssignmentConfig
            config={triggerConfig || {}}
            onChange={onTriggerConfigChange}
            campuses={filterOptions.campuses}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <TriggerTypeSelector
        selectedType={triggerType}
        onChange={onTriggerTypeChange}
        disabled={disabled}
      />

      {triggerType && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-fg">Trigger Configuration</h4>
          {loading ? <Loader size="sm" label="Loading trigger options" /> : renderTriggerConfig()}
        </div>
      )}

      {triggerType && (
        <AlertPane tone="info" className="">
          <strong>Tip:</strong> You can combine triggers with recipient filters below to target
          specific segments (e.g., "7-day follow-up for Frisco campus only").
        </AlertPane>
      )}
    </div>
  );
};

export default TriggerConfiguration;
